import React, { useState, useEffect } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Alert,
  RefreshControl } from 'react-native'
import { Coins, ClipboardList, RotateCcw } from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import { useLanguage } from '@fliisi/i18n'
import { calculateMarketPayouts, processMarketPayouts, formatCurrency } from '../lib/stripe'
import { supabase } from '../lib/supabase'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MarketPayouts'>
  route: RouteProp<RootStackParamList, 'MarketPayouts'>
}

interface PayoutData {
  seller_id: string
  seller_name: string
  seller_email: string
  stripe_account_id: string | null
  gross_amount: number
  commission_amount: number
  net_amount: number
  items_sold: number
  can_pay: boolean
}

interface PayoutHistory {
  id: string
  seller_id: string
  gross_sales: number
  commission_amount: number
  net_amount: number
  status: string
  period_start: string
  period_end: string
  created_at: string
  seller?: {
    display_name: string | null
    first_name: string | null
    last_name: string | null
    email: string
  }
}

export default function MarketPayoutsScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t } = useLanguage()

  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [payouts, setPayouts] = useState<PayoutData[]>([])
  const [history, setHistory] = useState<PayoutHistory[]>([])
  const [summary, setSummary] = useState<{
    total_gross: number
    total_commission: number
    total_net: number
    seller_count: number
    sellers_with_stripe: number
    sellers_without_stripe: number
    items_count: number
  } | null>(null)
  const [stripeBalance, setStripeBalance] = useState<number | null>(null)
  const [insufficientFunds, setInsufficientFunds] = useState(false)
  const [topUpNeeded, setTopUpNeeded] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadPayouts()
    loadHistory()
  }, [marketId])

  const loadPayouts = async () => {
    try {
      setError(null)
      const result = await calculateMarketPayouts(marketId)

      if (result.error) {
        setError(result.error)
        return
      }

      setPayouts(result.payouts || [])
      setSummary(result.summary || null)
      setStripeBalance(result.stripe_balance ?? null)
      setInsufficientFunds(result.insufficient_funds || false)
      setTopUpNeeded(result.top_up_needed || 0)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const loadHistory = async () => {
    try {
      const { data, error: historyError } = await supabase
        .from('payouts')
        .select(`
          id,
          seller_id,
          gross_sales,
          commission_amount,
          net_amount,
          status,
          period_start,
          period_end,
          created_at,
          seller:users!payouts_seller_id_fkey (
            display_name,
            first_name,
            last_name,
            email
          )
        `)
        .eq('flea_market_id', marketId)
        .order('created_at', { ascending: false })
        .limit(50)

      if (historyError) {
        console.error('Error loading history:', historyError)
        return
      }

      const formattedHistory = (data || []).map(item => ({
        ...item,
        seller: Array.isArray(item.seller) ? item.seller[0] : item.seller }))

      setHistory(formattedHistory)
    } catch (err) {
      console.error('Error loading history:', err)
    }
  }

  const onRefresh = () => {
    setRefreshing(true)
    loadPayouts()
    loadHistory()
  }

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(message)
    } else {
      Alert.alert(title, message)
    }
  }

  const confirmProcess = () => {
    const message = `This will send ${formatCurrency(summary?.total_net || 0)} to ${summary?.sellers_with_stripe || 0} sellers. Continue?`

    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        handleProcessPayouts()
      }
    } else {
      Alert.alert(
        t('confirm_payouts') || 'Confirm Payouts',
        message,
        [
          { text: t('cancel') || 'Cancel', style: 'cancel' },
          { text: t('process') || 'Process', onPress: handleProcessPayouts },
        ]
      )
    }
  }

  const handleProcessPayouts = async () => {
    setProcessing(true)
    try {
      const result = await processMarketPayouts(marketId)

      if (result.error) {
        showAlert(t('error') || 'Error', result.error)
        return
      }

      const successCount = result.summary?.processed_count || 0
      const failedCount = result.summary?.failed_count || 0

      showAlert(
        t('success') || 'Success',
        `Processed ${successCount} payouts. ${failedCount > 0 ? `${failedCount} failed.` : ''}`
      )

      // Reload data
      loadPayouts()
    } catch (err: any) {
      showAlert(t('error') || 'Error', err.message)
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <Text style={styles.headerTitle}>{t('seller_payouts') || 'Seller Payouts'}</Text>
          <TouchableOpacity
            style={styles.refundsButton}
            onPress={() => navigation.navigate('MarketRefunds', { marketId })}
          >
            <RotateCcw size={16} color={theme.colors.status.error} />
            <Text style={styles.refundsButtonText}>{t('refunds') || 'Refunds'}</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.tabContainer}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'pending' && styles.tabActive]}
            onPress={() => setActiveTab('pending')}
          >
            <Text style={[styles.tabText, activeTab === 'pending' && styles.tabTextActive]}>
              {t('pending') || 'Pending'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'history' && styles.tabActive]}
            onPress={() => setActiveTab('history')}
          >
            <Text style={[styles.tabText, activeTab === 'history' && styles.tabTextActive]}>
              {t('history') || 'History'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {error && (
          <View style={styles.errorBanner}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {activeTab === 'pending' && (
          <>
            {/* Balance & Summary Card */}
            {summary && (
              <View style={styles.summaryCard}>
                <Text style={styles.summaryTitle}>{t('payout_summary') || 'Payout Summary'}</Text>

                {stripeBalance !== null && (
                  <View style={styles.balanceRow}>
                    <Text style={styles.balanceLabel}>{t('stripe_balance') || 'Stripe Balance'}</Text>
                    <Text style={[styles.balanceAmount, insufficientFunds && styles.balanceInsufficient]}>
                      {formatCurrency(stripeBalance)}
                    </Text>
                  </View>
                )}

                {insufficientFunds && (
                  <View style={styles.warningBanner}>
                    <Text style={styles.warningText}>
                      {t('insufficient_funds') || 'Insufficient funds.'} {t('top_up_needed') || 'Top up needed:'} {formatCurrency(topUpNeeded)}
                    </Text>
                    <TouchableOpacity style={styles.topUpButton}>
                      <Text style={styles.topUpButtonText}>{t('top_up') || 'Top Up Balance'}</Text>
                    </TouchableOpacity>
                  </View>
                )}

                <View style={styles.summaryGrid}>
                  <View style={styles.summaryItem}>
                    <Text style={styles.summaryValue}>{summary.items_count}</Text>
                    <Text style={styles.summaryLabel}>{t('items_sold') || 'Items Sold'}</Text>
                  </View>
                  <View style={styles.summaryItem}>
                    <Text style={styles.summaryValue}>{formatCurrency(summary.total_gross)}</Text>
                    <Text style={styles.summaryLabel}>{t('gross_sales') || 'Gross Sales'}</Text>
                  </View>
                  <View style={styles.summaryItem}>
                    <Text style={styles.summaryValue}>{formatCurrency(summary.total_commission)}</Text>
                    <Text style={styles.summaryLabel}>{t('commission') || 'Commission'}</Text>
                  </View>
                  <View style={styles.summaryItem}>
                    <Text style={[styles.summaryValue, styles.summaryValueHighlight]}>
                      {formatCurrency(summary.total_net)}
                    </Text>
                    <Text style={styles.summaryLabel}>{t('to_pay_sellers') || 'To Pay Sellers'}</Text>
                  </View>
                </View>

                <View style={styles.sellerStats}>
                  <Text style={styles.sellerStatsText}>
                    {summary.seller_count} {t('sellers') || 'sellers'} • {' '}
                    <Text style={styles.statsGreen}>{summary.sellers_with_stripe} {t('ready') || 'ready'}</Text>
                    {summary.sellers_without_stripe > 0 && (
                      <Text style={styles.statsOrange}> • {summary.sellers_without_stripe} {t('need_setup') || 'need setup'}</Text>
                    )}
                  </Text>
                </View>
              </View>
            )}

            {/* Seller List */}
            <View style={styles.sellersSection}>
              <Text style={styles.sectionTitle}>{t('pending_payouts') || 'Pending Payouts'}</Text>

              {payouts.length === 0 ? (
                <View style={styles.emptyState}>
                  <View style={styles.emptyIconContainer}>
                    <Coins size={48} color={theme.colors.text.secondary} />
                  </View>
                  <Text style={styles.emptyText}>{t('no_pending_payouts') || 'No pending payouts'}</Text>
                  <Text style={styles.emptySubtext}>
                    {t('payouts_appear_here') || 'Payouts will appear here when items are sold'}
                  </Text>
                </View>
              ) : (
                payouts.map((payout) => (
                  <View key={payout.seller_id} style={styles.sellerCard}>
                    <View style={styles.sellerHeader}>
                      <View>
                        <Text style={styles.sellerName}>{payout.seller_name}</Text>
                        <Text style={styles.sellerEmail}>{payout.seller_email}</Text>
                      </View>
                      <View style={[styles.statusBadge, payout.can_pay ? styles.statusReady : styles.statusPending]}>
                        <Text style={styles.statusBadgeText}>
                          {payout.can_pay ? (t('ready') || 'Ready') : (t('no_stripe') || 'No Stripe')}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.sellerDetails}>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>{t('items_sold') || 'Items sold'}</Text>
                        <Text style={styles.detailValue}>{payout.items_sold}</Text>
                      </View>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>{t('gross') || 'Gross'}</Text>
                        <Text style={styles.detailValue}>{formatCurrency(payout.gross_amount)}</Text>
                      </View>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>{t('commission') || 'Commission'}</Text>
                        <Text style={styles.detailValueMuted}>-{formatCurrency(payout.commission_amount)}</Text>
                      </View>
                      <View style={[styles.detailRow, styles.detailRowTotal]}>
                        <Text style={styles.detailLabelBold}>{t('net_payout') || 'Net payout'}</Text>
                        <Text style={styles.detailValueBold}>{formatCurrency(payout.net_amount)}</Text>
                      </View>
                    </View>
                  </View>
                ))
              )}
            </View>
          </>
        )}

        {activeTab === 'history' && (
          <View style={styles.historySection}>
            {history.length === 0 ? (
              <View style={styles.emptyState}>
                <View style={styles.emptyIconContainer}>
                  <ClipboardList size={48} color={theme.colors.text.secondary} />
                </View>
                <Text style={styles.emptyText}>{t('no_payout_history') || 'No payout history'}</Text>
                <Text style={styles.emptySubtext}>
                  {t('history_appear_here') || 'Processed payouts will appear here'}
                </Text>
              </View>
            ) : (
              history.map((item) => (
                <View key={item.id} style={styles.historyCard}>
                  <View style={styles.historyHeader}>
                    <View>
                      <Text style={styles.historySellerName}>
                        {item.seller?.display_name ||
                          (item.seller?.first_name || item.seller?.last_name
                            ? `${item.seller?.first_name || ''} ${item.seller?.last_name || ''}`.trim()
                            : 'Unknown Seller')}
                      </Text>
                      <Text style={styles.historyDate}>
                        {new Date(item.created_at).toLocaleDateString('fi-FI', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit' })}
                      </Text>
                    </View>
                    <View style={[styles.historyStatusBadge, item.status === 'completed' ? styles.historyStatusCompleted : styles.historyStatusPending]}>
                      <Text style={styles.historyStatusText}>
                        {item.status === 'completed' ? (t('paid') || 'Paid') : item.status}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.historyDetails}>
                    <View style={styles.historyDetailRow}>
                      <Text style={styles.historyDetailLabel}>{t('period') || 'Period'}</Text>
                      <Text style={styles.historyDetailValue}>
                        {new Date(item.period_start).toLocaleDateString('fi-FI')} - {new Date(item.period_end).toLocaleDateString('fi-FI')}
                      </Text>
                    </View>
                    <View style={styles.historyDetailRow}>
                      <Text style={styles.historyDetailLabel}>{t('gross') || 'Gross'}</Text>
                      <Text style={styles.historyDetailValue}>{formatCurrency(item.gross_sales)}</Text>
                    </View>
                    <View style={styles.historyDetailRow}>
                      <Text style={styles.historyDetailLabel}>{t('commission') || 'Commission'}</Text>
                      <Text style={styles.historyDetailValueMuted}>-{formatCurrency(item.commission_amount)}</Text>
                    </View>
                    <View style={[styles.historyDetailRow, styles.historyDetailRowTotal]}>
                      <Text style={styles.historyDetailLabelBold}>{t('net_paid') || 'Net Paid'}</Text>
                      <Text style={styles.historyDetailValueBold}>{formatCurrency(item.net_amount)}</Text>
                    </View>
                  </View>
                </View>
              ))
            )}
          </View>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>

      {/* Process Button */}
      {activeTab === 'pending' && payouts.length > 0 && summary && summary.sellers_with_stripe > 0 && (
        <View style={styles.bottomBar}>
          <TouchableOpacity
            style={[
              styles.processButton,
              (processing || insufficientFunds) && styles.processButtonDisabled,
            ]}
            onPress={confirmProcess}
            disabled={processing || insufficientFunds}
          >
            {processing ? (
              <Spinner color="#fff" />
            ) : (
              <Text style={styles.processButtonText}>
                {insufficientFunds
                  ? (t('top_up_to_process') || 'Top up to process')
                  : `${t('process_payouts') || 'Process Payouts'} (${formatCurrency(summary.total_net)})`
                }
              </Text>
            )}
          </TouchableOpacity>
        </View>
      )}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary },
  header: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 16,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12 },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  refundsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFEBEE',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.md,
    gap: 6 },
  refundsButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.status.error },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: 4 },
  tab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: theme.borderRadius.sm,
    alignItems: 'center' },
  tabActive: {
    backgroundColor: theme.colors.background.card },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  tabTextActive: {
    color: theme.colors.text.primary,
    fontWeight: '600' },
  content: {
    flex: 1,
    padding: 16 },
  errorBanner: {
    backgroundColor: '#fee2e2',
    padding: 12,
    borderRadius: theme.borderRadius.md,
    marginBottom: 16 },
  errorText: {
    color: '#dc2626',
    fontSize: 14 },
  summaryCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: theme.colors.ui.borderLight },
  summaryTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 16 },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight,
    marginBottom: 12 },
  balanceLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  balanceAmount: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.text.primary },
  balanceInsufficient: {
    color: '#dc2626' },
  warningBanner: {
    backgroundColor: '#fef3c7',
    padding: 12,
    borderRadius: theme.borderRadius.md,
    marginBottom: 16 },
  warningText: {
    color: '#92400e',
    fontSize: 14,
    marginBottom: 8 },
  topUpButton: {
    backgroundColor: '#f59e0b',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: theme.borderRadius.md,
    alignSelf: 'flex-start' },
  topUpButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' },
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8 },
  summaryItem: {
    width: '50%',
    paddingHorizontal: 8,
    marginBottom: 16 },
  summaryValue: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  summaryValueHighlight: {
    color: theme.colors.merchant.primary },
  summaryLabel: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  sellerStats: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight },
  sellerStatsText: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  statsGreen: {
    color: '#16a34a' },
  statsOrange: {
    color: '#ea580c' },
  sellersSection: {
    marginBottom: 20 },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 12 },
  emptyState: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg },
  emptyIconContainer: {
    marginBottom: 16 },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  emptySubtext: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'center' },
  sellerCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.ui.borderLight },
  sellerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12 },
  sellerName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  sellerEmail: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.full },
  statusReady: {
    backgroundColor: '#dcfce7' },
  statusPending: {
    backgroundColor: '#fef3c7' },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.text.primary },
  sellerDetails: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: 12 },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6 },
  detailRowTotal: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight,
    marginBottom: 0 },
  detailLabel: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  detailLabelBold: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  detailValue: {
    fontSize: 13,
    color: theme.colors.text.primary },
  detailValueMuted: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  detailValueBold: {
    fontSize: 14,
    fontWeight: '700',
    color: theme.colors.merchant.primary },
  bottomSpacer: {
    height: 160 },
  bottomBar: {
    position: 'absolute',
    bottom: 80,
    left: 0,
    right: 0,
    padding: 16,
    backgroundColor: theme.colors.background.card,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight },
  processButton: {
    backgroundColor: theme.colors.merchant.primary,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center' },
  processButtonDisabled: {
    opacity: 0.6 },
  processButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  historySection: {
    flex: 1 },
  historyCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.ui.borderLight },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12 },
  historySellerName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  historyDate: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  historyStatusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.full },
  historyStatusCompleted: {
    backgroundColor: '#dcfce7' },
  historyStatusPending: {
    backgroundColor: '#fef3c7' },
  historyStatusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#16a34a' },
  historyDetails: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: 12 },
  historyDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6 },
  historyDetailRowTotal: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight,
    marginBottom: 0 },
  historyDetailLabel: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  historyDetailLabelBold: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  historyDetailValue: {
    fontSize: 13,
    color: theme.colors.text.primary },
  historyDetailValueMuted: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  historyDetailValueBold: {
    fontSize: 14,
    fontWeight: '700',
    color: '#16a34a' } })
